
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import SessionsModule from '@/store/modules/Sessions';
import UserModule from '@/store/modules/User';
import PhotosModule from '@/store/modules/Photos';
import SessionListElement from '@/model/SessionListElement';
import Folder from '@/model/Folder';
import ConfirmDelete from '@/components/session/ConfirmDelete.vue';
import ConfirmationModel from '@/components/ConfirmationModel';

const sessionsModule = getModule(SessionsModule, store);
const userModule = getModule(UserModule, store);
const photosModule = getModule(PhotosModule, store);

@Component({
    components: {
        ConfirmDelete,
    },
})
export default class EditFolderModal extends Vue {
    @Prop({ type: Boolean, default: false })
    public value!: boolean;

    @Prop({ type: Object, default: null })
    public folder!: Folder | null;

    public folderName: string = '';
    public selectedSessionIds: string[] = [];
    public updating: boolean = false;
    public deleteConfirmationModel: ConfirmationModel = new ConfirmationModel();
    public rules = {
        required: (value: string) => !!value || 'Pole wymagane',
    };
    private lastDeselectedSessionId: string | null = null;

    get dialog() {
        return this.value;
    }

    set dialog(value: boolean) {
        if (!value) {
            this.close();
        }
    }

    get availableSessions(): SessionListElement[] {
        if (!userModule.user) {
            return [];
        }

        // Get all sessions (including those in other folders, but excluding those in this folder)
        const allUserSessions = sessionsModule.sortedSessions[userModule.user.id] || [];
        const sessionsNotInOtherFolders = sessionsModule.sessionsNotInFolders[userModule.user.id] || [];
        const currentFolderSessionIds = this.folder?.sessionIds || [];

        // Combine sessions not in folders with sessions currently in this folder
        const availableSessionIds = new Set([
            ...sessionsNotInOtherFolders.map((s) => s.id),
            ...currentFolderSessionIds,
        ]);

        return allUserSessions.filter((session) => availableSessionIds.has(session.id));
    }

    get canUpdate(): boolean {
        return this.folderName.trim().length > 0 && this.selectedSessionIds.length > 0;
    }

    public getSessionThumbnail(session: SessionListElement): string | undefined {
        const sessionPhotosList = photosModule.sessionPhotos[session.id];
        const sessionThumbnail = session.thumbnail;
        
        if (
            sessionPhotosList &&
            sessionPhotosList.length > 0 &&
            sessionThumbnail &&
            sessionThumbnail.photoId &&
            sessionThumbnail.photoId.trim() !== '' &&
            sessionPhotosList.some(({ id }) => id === sessionThumbnail.photoId)
        ) {
            return sessionPhotosList
                .find(({ id }) => id === sessionThumbnail.photoId)!
                .smallThumbUrl;
        } else if (sessionThumbnail && sessionThumbnail.url && sessionThumbnail.url.trim() !== '') {
            return sessionThumbnail.url;
        } else if (sessionPhotosList && sessionPhotosList.length > 0) {
            return sessionPhotosList[0].smallThumbUrl;
        }
        
        return undefined;
    }

    @Watch('value')
    public onDialogChange(newValue: boolean) {
        if (newValue && this.folder) {
            this.resetForm();
        }
    }

    @Watch('folder')
    public onFolderChange(newFolder: Folder | null) {
        if (newFolder && this.value) {
            this.resetForm();
        }
    }

    @Watch('selectedSessionIds')
    public onSelectedSessionsChange(newSessionIds: string[], oldSessionIds: string[]) {
        // Check if we're deselecting the last session
        if (oldSessionIds.length > 0 && newSessionIds.length === 0) {
            // Find which session was just deselected
            this.lastDeselectedSessionId = oldSessionIds.find((id) => !newSessionIds.includes(id)) || null;
            this.showDeleteConfirmation();
        }
    }

    public async updateFolder() {
        if (!this.canUpdate || !userModule.user || !this.folder) {
            return;
        }

        this.updating = true;

        try {
            const updatedFolder: Folder = {
                ...this.folder,
                name: this.folderName.trim(),
                sessionIds: this.selectedSessionIds,
            };

            await sessionsModule.updateFolderAndSave({
                userId: userModule.user.id,
                folder: updatedFolder,
            });

            this.$emit('folder-updated');
            this.close();
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error('Error updating folder:', error);
            // You might want to show a toast notification here
        } finally {
            this.updating = false;
        }
    }

    public close() {
        this.$emit('input', false);
        this.resetForm();
    }

    public handleEnterKey() {
        if (this.canUpdate && !this.updating) {
            this.updateFolder();
        }
    }

    public async showDeleteConfirmation() {
        const confirmed = await this.deleteConfirmationModel.confirm('Czy chcesz usunąć folder?');

        if (confirmed) {
            this.deleteFolder();
        } else {
            // Restore only the last deselected session instead of all sessions
            if (this.lastDeselectedSessionId) {
                this.selectedSessionIds = [this.lastDeselectedSessionId];
                this.lastDeselectedSessionId = null;
            }
        }
    }

    public async deleteFolder() {
        if (!userModule.user || !this.folder) {
            return;
        }

        this.updating = true;
        this.$emit('folder-processing', true);
        await this.$nextTick();

        try {
            await sessionsModule.deleteFolder({
                userId: userModule.user.id,
                folderId: this.folder.id,
            });

            this.$emit('folder-deleted');
            this.close();
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error('Error deleting folder:', error);
        } finally {
            this.updating = false;
            this.$emit('folder-processing', false);
        }
    }

    private resetForm() {
        if (this.folder) {
            this.folderName = this.folder.name;
            this.selectedSessionIds = [...this.folder.sessionIds];
        } else {
            this.folderName = '';
            this.selectedSessionIds = [];
        }
        this.updating = false;
    }

    public toggleSessionSelection(sessionId: string) {
        if (this.updating) {
            return;
        }
        const index = this.selectedSessionIds.indexOf(sessionId);
        if (index > -1) {
            this.selectedSessionIds.splice(index, 1);
        } else {
            this.selectedSessionIds.push(sessionId);
        }
    }
}
